import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Typography, Spin, Card, Space, Divider, Tag, Form, Input, message } from "antd";
import { createClient, SupabaseClient } from "@supabase/supabase-js";
import TransitionWrapper from "../../Components/TransitionWrapper";
import PageMetadata from "../../Components/PageMetaData/PageMetaData";
import Button from "../../Components/Atoms/DynamicButton";
import "./index.scss";

interface AuthorizationDetails {
  client_id: string;
  client_name?: string;
  redirect_uri: string;
  scopes?: string[];
  state?: string;
  [key: string]: any; // Allow additional properties from Supabase
}

const SupabaseOAuthApproval = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [status, setStatus] = useState<
    "loading" | "checking_auth" | "login" | "consent" | "processing" | "error"
  >("loading");
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [authorizationDetails, setAuthorizationDetails] =
    useState<AuthorizationDetails | null>(null);
  const authorization_id = searchParams.get("authorization_id");

  // Get Supabase configuration from environment variables
  const supabaseUrl =
    process.env.REACT_APP_SUPABASE_URL ||
    process.env.NEXT_PUBLIC_SUPABASE_URL;
  const supabaseKey =
    process.env.REACT_APP_SUPABASE_ANON_KEY ||
    process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  // Create Supabase client with session persistence (needed for OAuth approval)
  const supabase = useMemo<SupabaseClient | null>(() => {
    if (!supabaseUrl || !supabaseKey) {
      return null;
    }
    return createClient(supabaseUrl, supabaseKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true, // Enable session persistence for OAuth approval
      },
    });
  }, [supabaseUrl, supabaseKey]);

  // Check Supabase session and fetch authorization details
  useEffect(() => {
    const initializeAuthorization = async () => {
      if (!authorization_id) {
        setStatus("error");
        setErrorMessage("Missing authorization_id parameter");
        return;
      }

      if (!supabase) {
        setStatus("error");
        setErrorMessage(
          "Supabase configuration missing. Please set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY environment variables."
        );
        return;
      }

      try {
        setStatus("checking_auth");

        // Check if user has a valid Supabase session
        const { data: sessionData, error: sessionError } =
          await supabase.auth.getSession();

        if (sessionError) {
          console.error("Error checking session:", sessionError);
          setStatus("login");
          return;
        }

        if (!sessionData?.session) {
          // No active session, show login form
          setStatus("login");
          return;
        }

        // User is authenticated, fetch authorization details
        const { data, error } =
          await supabase.auth.oauth.getAuthorizationDetails(authorization_id);

        if (error) {
          console.error(
            "Error fetching authorization details:",
            error
          );
          setStatus("error");
          setErrorMessage(
            error.message || "Failed to fetch authorization details"
          );
          return;
        }

        if (!data) {
          setStatus("error");
          setErrorMessage("Authorization details not found");
          return;
        }

        console.log("Authorization details:", data);
        
        // If redirect_url is present in the response, it means consent was already given
        // Redirect directly without calling approveAuthorization
        const redirectUrl = (data as any)?.redirect_url;
        if (redirectUrl) {
          console.log("Consent already given, redirecting to:", redirectUrl);
          window.location.href = redirectUrl;
          return;
        }

        // Map Supabase response to our interface
        const details: AuthorizationDetails = {
          client_id: (data as any).client_id || "",
          client_name: (data as any).client_name,
          redirect_uri: (data as any).redirect_uri || "",
          scopes: (data as any).scopes || [],
          state: (data as any).state,
          ...data, // Include any additional properties
        };

        setAuthorizationDetails(details);
        setStatus("consent");
      } catch (error: any) {
        console.error("Error initializing authorization:", error);
        setStatus("error");
        setErrorMessage(error.message || "An unexpected error occurred");
      }
    };

    initializeAuthorization();
  }, [authorization_id, supabase]);

  // Handle login form submission
  const handleLogin = async (values: { email: string; password: string }) => {
    if (!supabase) {
      message.error("Supabase configuration missing");
      return;
    }

    try {
      setIsLoggingIn(true);
      setErrorMessage("");

      const { data, error } = await supabase.auth.signInWithPassword({
        email: values.email,
        password: values.password,
      });

      if (error) {
        console.error("Login error:", error);
        setErrorMessage(error.message || "Failed to sign in");
        message.error(error.message || "Failed to sign in");
        setIsLoggingIn(false);
        return;
      }

      if (data?.session) {
        message.success("Successfully signed in");
        // Re-fetch authorization details now that we have a session
        if (authorization_id) {
          const { data: authData, error: authError } =
            await supabase.auth.oauth.getAuthorizationDetails(authorization_id);

          if (authError) {
            console.error("Error fetching authorization details:", authError);
            setStatus("error");
            setErrorMessage(
              authError.message || "Failed to fetch authorization details"
            );
            setIsLoggingIn(false);
            return;
          }

          if (!authData) {
            setStatus("error");
            setErrorMessage("Authorization details not found");
            setIsLoggingIn(false);
            return;
          }

          // If redirect_url is present in the response, it means consent was already given
          // Redirect directly without calling approveAuthorization
          const redirectUrl = (authData as any)?.redirect_url;
          if (redirectUrl) {
            console.log("Consent already given, redirecting to:", redirectUrl);
            setIsLoggingIn(false);
            window.location.href = redirectUrl;
            return;
          }

          const details: AuthorizationDetails = {
            client_id: (authData as any).client_id || "",
            client_name: (authData as any).client_name,
            redirect_uri: (authData as any).redirect_uri || "",
            scopes: (authData as any).scopes || [],
            state: (authData as any).state,
            ...authData,
          };
          console.log("Authorization details:", details);

          setAuthorizationDetails(details);
          setStatus("consent");
        }
      }
      setIsLoggingIn(false);
    } catch (error: any) {
      console.error("Login error:", error);
      setErrorMessage(error.message || "An unexpected error occurred");
      message.error(error.message || "An unexpected error occurred");
      setIsLoggingIn(false);
    }
  };

  const handleApprove = async () => {
    if (!authorization_id || !supabase) {
      setStatus("error");
      setErrorMessage("Missing required parameters");
      return;
    }

    console.log("Approving authorization with ID:", authorization_id);
    try {
      setStatus("processing");
      setIsProcessing(true);

      // Approve the authorization (requires valid Supabase session)
      const { data, error } = await supabase.auth.oauth.approveAuthorization(
        authorization_id
      );

      if (error) {
        console.error("Error approving authorization:", error.code, error.message, error.status);
        setStatus("error");
        setIsProcessing(false);
        setErrorMessage(error.message || "Failed to approve authorization");
        return;
      }

      console.log("Authorization approved, redirecting to:", data);

      // Redirect to the URL returned by Supabase
      // The response may contain redirect_to or redirect_url
      const redirectUrl = (data as any)?.redirect_to || (data as any)?.redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        setStatus("error");
        setErrorMessage("No redirect URL returned from authorization approval");
      }
    } catch (error: any) {
      console.error("Error approving authorization:", error);
      setStatus("error");
      setIsProcessing(false);
      setErrorMessage(error.message || "An unexpected error occurred");
    }
  };

  const handleDeny = async () => {
    if (!authorization_id || !supabase) {
      setStatus("error");
      setErrorMessage("Missing required parameters");
      return;
    }

    try {
      setStatus("processing");
      setIsProcessing(true);

      // Deny the authorization (requires valid Supabase session)
      const { data, error } = await supabase.auth.oauth.denyAuthorization(
        authorization_id
      );

      if (error) {
        console.error("Error denying authorization:", error);
        setStatus("error");
        setErrorMessage(error.message || "Failed to deny authorization");
        return;
      }

      console.log("Authorization denied, redirecting to:", data);

      // Redirect to the URL returned by Supabase
      // The response may contain redirect_to or redirect_url
      const redirectUrl = (data as any)?.redirect_to || (data as any)?.redirect_url;
      if (redirectUrl) {
        window.location.href = redirectUrl;
      } else {
        setStatus("error");
        setErrorMessage("No redirect URL returned from authorization denial");
      }
    } catch (error: any) {
      console.error("Error denying authorization:", error);
      setStatus("error");
      setIsProcessing(false);
      setErrorMessage(error.message || "An unexpected error occurred");
    }
  };

  const getScopeDescription = (scope: string): string => {
    const scopeDescriptions: Record<string, string> = {
      openid: "Access your identity information",
      email: "Access your email address",
      profile: "Access your profile information (name, picture, etc.)",
      phone: "Access your phone number",
    };
    return scopeDescriptions[scope] || scope;
  };

  return (
    <>
      <PageMetadata
        title="OAuth Authorization"
        description="Review and approve OAuth authorization request"
      />
      <TransitionWrapper transitionType="slide-up">
        <div className="supabase-oauth-approval-page">
          {(status === "loading" || status === "checking_auth") && (
            <div className="approval-content">
              <Spin size="large" />
              <Typography.Title level={4} style={{ marginTop: 24 }}>
                {status === "loading"
                  ? "Loading authorization request..."
                  : "Checking authentication..."}
              </Typography.Title>
              <Typography.Text type="secondary">
                Please wait while we process your request.
              </Typography.Text>
            </div>
          )}

          {status === "login" && (
            <Card
              className="login-card"
              title={
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Sign In Required
                </Typography.Title>
              }
            >
              <Typography.Paragraph type="secondary" style={{ marginBottom: 24 }}>
                Please sign in to your Supabase account to authorize this application.
              </Typography.Paragraph>

              <Form
                form={form}
                name="login"
                onFinish={handleLogin}
                layout="vertical"
                autoComplete="off"
              >
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input
                    placeholder="Enter your email"
                    size="large"
                    disabled={isLoggingIn}
                  />
                </Form.Item>

                <Form.Item
                  name="password"
                  label="Password"
                  rules={[
                    { required: true, message: "Please enter your password" },
                  ]}
                >
                  <Input.Password
                    placeholder="Enter your password"
                    size="large"
                    disabled={isLoggingIn}
                  />
                </Form.Item>

                {errorMessage && (
                  <Typography.Text type="danger" style={{ marginBottom: 16, display: "block" }}>
                    {errorMessage}
                  </Typography.Text>
                )}

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoggingIn}
                    block
                  >
                    Sign In
                  </Button>
                </Form.Item>
              </Form>
            </Card>
          )}

          {status === "consent" && authorizationDetails && (
            <Card
              className="consent-card"
              title={
                <Typography.Title level={3} style={{ margin: 0 }}>
                  Authorize Application
                </Typography.Title>
              }
            >
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                <div>
                  <Typography.Text strong>
                    {authorizationDetails.client_name ||
                      authorizationDetails.client_id}
                  </Typography.Text>{" "}
                  is requesting access to your account.
                </div>

                {authorizationDetails.redirect_uri && (
                  <div>
                    <Typography.Text type="secondary" strong>
                      Redirect URI:
                    </Typography.Text>
                    <br />
                    <Typography.Text code>
                      {authorizationDetails.redirect_uri}
                    </Typography.Text>
                  </div>
                )}

                {authorizationDetails.scopes &&
                  authorizationDetails.scopes.length > 0 && (
                    <div>
                      <Typography.Text type="secondary" strong>
                        Requested Permissions:
                      </Typography.Text>
                      <div style={{ marginTop: 8 }}>
                        {authorizationDetails.scopes.map((scope) => (
                          <Tag key={scope} style={{ marginBottom: 4 }}>
                            {getScopeDescription(scope)}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  )}

                <Divider />

                <Space size="middle" style={{ width: "100%", justifyContent: "flex-end" }}>
                  <Button 
                    onClick={handleDeny} 
                    disabled={isProcessing}
                  >
                    Deny
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleApprove}
                    loading={isProcessing}
                  >
                    Approve
                  </Button>
                </Space>
              </Space>
            </Card>
          )}

          {status === "processing" && (
            <div className="approval-content">
              <Spin size="large" />
              <Typography.Title level={4} style={{ marginTop: 24 }}>
                Processing your request...
              </Typography.Title>
              <Typography.Text type="secondary">
                Please wait while we complete the authorization.
              </Typography.Text>
            </div>
          )}

          {status === "error" && (
            <Card className="error-card">
              <Typography.Title level={4} type="danger">
                Authorization Error
              </Typography.Title>
              <Typography.Paragraph>{errorMessage}</Typography.Paragraph>
              <Button
                type="primary"
                onClick={() => {
                  setStatus("loading");
                  setErrorMessage("");
                  // Re-initialize to check session again
                  if (supabase && authorization_id) {
                    supabase.auth.getSession().then(({ data: sessionData }) => {
                      if (sessionData?.session) {
                        // Re-fetch authorization details
                        supabase.auth.oauth
                          .getAuthorizationDetails(authorization_id)
                          .then(({ data, error }) => {
                            if (error || !data) {
                              setStatus("error");
                              setErrorMessage(
                                error?.message || "Failed to fetch authorization details"
                              );
                            } else {
                              // If redirect_url is present in the response, it means consent was already given
                              // Redirect directly without calling approveAuthorization
                              const redirectUrl = (data as any)?.redirect_url;
                              if (redirectUrl) {
                                console.log("Consent already given, redirecting to:", redirectUrl);
                                window.location.href = redirectUrl;
                                return;
                              }

                              const details: AuthorizationDetails = {
                                client_id: (data as any).client_id || "",
                                client_name: (data as any).client_name,
                                redirect_uri: (data as any).redirect_uri || "",
                                scopes: (data as any).scopes || [],
                                state: (data as any).state,
                                ...data,
                              };

                              setAuthorizationDetails(details);
                              setStatus("consent");
                            }
                          });
                      } else {
                        setStatus("login");
                      }
                    });
                  }
                }}
              >
                Retry
              </Button>
            </Card>
          )}
        </div>
      </TransitionWrapper>
    </>
  );
};

export default SupabaseOAuthApproval;
